import { CurrencyIcon, MMButton, SelectAccount } from '@components'
import BackArrowIcon from '@icons/arrow-left.svg?react'
import cn from '@integrations/classnames'
import { useCurrencies } from 'mmfintech-backend-api'
import type {
  AccountBalanceResponse,
  BankAccountFeeResponse,
  BankAccountScheme,
  IbanFeeState
} from 'mmfintech-backend-api/types'
import { formatMoney, tr } from 'mmfintech-commons'
import { ErrorDisplay } from 'mmfintech-portal-commons'
import styles from './IbanRequest.module.scss'
import RecurringFees from './RecurringFees'

interface Props {
  selectedScheme: BankAccountScheme
  selectedCurrency: string
  selectedAccount: AccountBalanceResponse
  payFromAccounts: AccountBalanceResponse[]
  payFromAccount: AccountBalanceResponse | null
  setPayFromAccount: (account: AccountBalanceResponse) => void
  feeState: IbanFeeState
  fees?: BankAccountFeeResponse[]
  submitting: boolean
  error?: unknown
  onBack: () => void
  onSubmit: () => void
  onTopUp: () => void
  onOpenFeeTerms: () => void
}

function ConfirmStep({
  selectedScheme,
  selectedCurrency,
  selectedAccount,
  payFromAccounts,
  payFromAccount,
  setPayFromAccount,
  feeState,
  fees,
  submitting,
  error,
  onBack,
  onSubmit,
  onTopUp,
  onOpenFeeTerms
}: Props) {
  const { getCurrencyPrecision } = useCurrencies()

  // Single source of truth for the fee tri-state (derived once in IbanRequestFlow via deriveIbanFeeState).
  const { feeValue, feeCurrency, feeApplies, feePaid, showFee } = feeState
  const precision = getCurrencyPrecision(feeCurrency)
  // Only insufficient once a funding account is actually chosen and it can't cover the fee.
  const insufficient = feeApplies && !!payFromAccount && Number(payFromAccount.balance ?? 0) < feeValue

  function renderSubmitLabel() {
    if (!feeApplies) return tr('FRONTEND.IBAN_REQUEST.ISSUE_BUTTON', 'Issue IBAN')
    if (insufficient) return tr('FRONTEND.IBAN_REQUEST.TOP_UP_BUTTON', 'Top up to continue')
    return tr('FRONTEND.IBAN_REQUEST.PAY_BUTTON', 'Pay {{AMOUNT}} & request', {
      AMOUNT: formatMoney(feeValue, feeCurrency, precision)
    })
  }

  return (
    <div className={cn(styles['iban-step-view'])}>
      <div className={cn(styles['iban-confirm-grid'])}>
        <div>
          {feeApplies ? (
            <>
              <div className={cn(styles['iban-fee-card'])}>
                <div className={cn(styles['iban-fee-row'], styles['total'])}>
                  <span className={cn(styles['iban-fee-label'])}>
                    {tr('FRONTEND.IBAN_REQUEST.FEE_TOTAL', 'Total (one-time)')}
                  </span>
                  <span className={cn(styles['iban-fee-value'], styles['total-val'])}>
                    {formatMoney(feeValue, feeCurrency, precision)}
                  </span>
                </div>
                <div className={cn(styles['iban-fee-note'])}>
                  {tr('FRONTEND.IBAN_REQUEST.FEE_NOTE', 'After this, all future IBANs are issued free.')}
                </div>
              </div>

              <div className={cn(styles['iban-pay-from'])}>
                <SelectAccount
                  accounts={payFromAccounts}
                  selectedAccount={payFromAccount ?? undefined}
                  setSelectedAccount={setPayFromAccount}
                  label={tr('FRONTEND.IBAN_REQUEST.PAY_FROM', 'Pay from')}
                  dataTest='iban-pay-from'
                  bordered
                />
              </div>

              {insufficient && (
                <div className={cn(styles['iban-warn-banner'])}>
                  <span className={cn(styles['iban-warn-icon'])}>!</span>
                  <span className={cn(styles['iban-warn-text'])}>
                    {tr(
                      'FRONTEND.IBAN_REQUEST.INSUFFICIENT',
                      'Insufficient balance. You need {{AMOUNT}} in your {{CURRENCY}} account to continue. Top up now — we will save your selections.',
                      { AMOUNT: formatMoney(feeValue, feeCurrency, precision), CURRENCY: feeCurrency }
                    )}
                  </span>
                </div>
              )}
            </>
          ) : (
            <div className={cn(styles['iban-free-banner'])}>
              {feePaid
                ? tr('FRONTEND.IBAN_REQUEST.FREE_BANNER', 'Issuance fee already paid. This IBAN is free.')
                : tr('FRONTEND.IBAN_REQUEST.NO_FEE_BANNER', 'No issuance fee for this IBAN.')}
            </div>
          )}

          {showFee && <RecurringFees fees={fees} consent />}
        </div>

        <div>
          <div className={cn(styles['iban-panel-label'])}>{tr('FRONTEND.IBAN_REQUEST.SUMMARY', 'Summary')}</div>
          <div className={cn(styles['iban-summary-rows'])}>
            <div className={cn(styles['iban-summary-row'])}>
              <span className={cn(styles['iban-summary-key'])}>
                {tr('FRONTEND.IBAN_REQUEST.NETWORK_TITLE_SHORT', 'Network')}
              </span>
              <span className={cn(styles['iban-summary-val'])}>
                {tr(`METADATA.PAYMENT_OPTIONS.${selectedScheme}`, selectedScheme)}
              </span>
            </div>
            <div className={cn(styles['iban-summary-row'])}>
              <span className={cn(styles['iban-summary-key'])}>
                {tr('FRONTEND.IBAN_REQUEST.CURRENCY_TITLE_SHORT', 'Currency')}
              </span>
              <span className={cn(styles['iban-summary-val'])}>
                <CurrencyIcon currency={selectedCurrency} size='20px' circle /> {selectedCurrency}
              </span>
            </div>
            <div className={cn(styles['iban-summary-row'])}>
              <span className={cn(styles['iban-summary-key'])}>
                {tr('FRONTEND.IBAN_REQUEST.ACCOUNT_TITLE_SHORT', 'Linked account')}
              </span>
              <span className={cn(styles['iban-summary-val'])}>{selectedAccount?.name}</span>
            </div>
            {feeApplies && (
              <div className={cn(styles['iban-summary-row'])}>
                <span className={cn(styles['iban-summary-key'])}>
                  {tr('FRONTEND.IBAN_REQUEST.ISSUANCE_FEE', 'Issuing fee')}
                </span>
                <span className={cn(styles['iban-summary-val'])}>{formatMoney(feeValue, feeCurrency, precision)}</span>
              </div>
            )}
          </div>

          <ErrorDisplay error={error} />

          <MMButton
            type='button'
            variant='primary'
            caption={renderSubmitLabel()}
            onClick={insufficient ? onTopUp : onSubmit}
            loading={submitting}
            data-test='iban-submit'
            fullWidth
          />

          <div className={cn(styles['iban-fine-print'])}>
            {tr('FRONTEND.IBAN_REQUEST.TERMS_PREFIX', 'By continuing you agree to the')}{' '}
            <span className={cn(styles['iban-link'])} onClick={onOpenFeeTerms} data-test='iban-fee-terms-link'>
              {tr('FRONTEND.IBAN_REQUEST.TERMS_LINK', 'IBAN issuing fee terms')}
            </span>
            .
          </div>

          <MMButton
            type='button'
            variant='secondary'
            leftIcon={BackArrowIcon}
            caption={tr('FRONTEND.BUTTONS.BACK', 'Back')}
            onClick={onBack}
            disabled={submitting}
            className={cn(styles['iban-ghost-back'], 'iban-ghost-back')}
            data-test='iban-back'
          />
        </div>
      </div>
    </div>
  )
}

export default ConfirmStep
